// ===========================================
// A staff-only reason for a late-fee decision (#372 plan A6.3)
// ===========================================
// One dialog for the three decisions that need a reason: waive a fee, put reminders on hold, and
// exempt a family. The reason is STAFF-ONLY (the family never sees it), 3–500 characters, and the
// server's refusal is shown inline (the hooks raise no snackbar for it).

import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { apiErrorMessage } from '../../utils/apiError';

export const LATE_FEE_REASON_MIN = 3;
export const LATE_FEE_REASON_MAX = 500;

export interface LateFeeReasonDialogProps {
  open: boolean;
  title: string;
  /** What happens, in words, shown above the box. */
  explanation: string[];
  label: string;
  confirmLabel: string;
  loading?: boolean;
  /** The server's refusal, shown inline. */
  error?: unknown;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}

export default function LateFeeReasonDialog({
  open,
  title,
  explanation,
  label,
  confirmLabel,
  loading = false,
  error,
  onConfirm,
  onClose,
}: LateFeeReasonDialogProps) {
  const [reason, setReason] = useState('');

  // Fresh every time it opens: a half-typed reason must not leak onto another invoice or family.
  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const trimmed = reason.trim();
  const valid = trimmed.length >= LATE_FEE_REASON_MIN && trimmed.length <= LATE_FEE_REASON_MAX;

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {explanation.map((line) => (
            <Typography key={line} variant="body2">
              {line}
            </Typography>
          ))}
          <TextField
            label={label}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            required
            inputProps={{ maxLength: LATE_FEE_REASON_MAX }}
            helperText={`Staff only: the family never sees this. ${trimmed.length}/${LATE_FEE_REASON_MAX}`}
          />
          {error ? <Alert severity="error">{apiErrorMessage(error, 'That did not work. Please try again.')}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => onConfirm(trimmed)} disabled={!valid || loading}>
          {loading ? 'Saving…' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
